/**
 * API Documentation Page
 *
 * Comprehensive API documentation for developers.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';

interface Endpoint {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  description: string;
  auth: boolean;
  requestBody?: string;
  responseBody: string;
  example?: string;
}

const endpoints: Record<string, Endpoint[]> = {
  'Authentication': [
    {
      method: 'POST',
      path: '/api/auth/register',
      description: 'Register a new user account',
      auth: false,
      requestBody: `{
  "email": "user@example.com",
  "password": "securePassword123!",
  "displayName": "John Doe"
}`,
      responseBody: `{
  "success": true,
  "user": {
    "id": "uuid",
    "email": "user@example.com",
    "displayName": "John Doe",
    "role": "user"
  }
}`,
    },
    {
      method: 'POST',
      path: '/api/auth/login',
      description: 'Authenticate and receive access token',
      auth: false,
      requestBody: `{
  "email": "user@example.com",
  "password": "securePassword123!"
}`,
      responseBody: `{
  "success": true,
  "accessToken": "eyJhbGciOiJIUzI1...",
  "user": {
    "id": "uuid",
    "email": "user@example.com",
    "role": "user"
  }
}`,
    },
    {
      method: 'POST',
      path: '/api/auth/logout',
      description: 'Invalidate the current session',
      auth: true,
      responseBody: `{
  "success": true
}`,
    },
    {
      method: 'GET',
      path: '/api/auth/me',
      description: 'Get the current authenticated user',
      auth: true,
      responseBody: `{
  "user": {
    "id": "uuid",
    "email": "user@example.com",
    "displayName": "John Doe",
    "role": "user",
    "emailVerified": true,
    "mfaEnabled": false
  }
}`,
    },
  ],
  'Forms': [
    {
      method: 'GET',
      path: '/api/forms',
      description: 'List all forms accessible to the user',
      auth: true,
      responseBody: `{
  "forms": [
    {
      "id": "uuid",
      "name": "Contact Form",
      "slug": "contact-form",
      "status": "published",
      "version": 1,
      "created_at": "2024-01-01T00:00:00Z",
      "updated_at": "2024-01-01T00:00:00Z"
    }
  ]
}`,
    },
    {
      method: 'POST',
      path: '/api/forms',
      description: 'Create a new form',
      auth: true,
      requestBody: `{
  "name": "Contact Form",
  "description": "A simple contact form",
  "schema": {
    "fields": [
      {
        "id": "name",
        "type": "text",
        "label": "Full Name",
        "required": true
      },
      {
        "id": "email",
        "type": "email",
        "label": "Email Address",
        "required": true
      },
      {
        "id": "message",
        "type": "textarea",
        "label": "Message",
        "required": true
      }
    ]
  }
}`,
      responseBody: `{
  "id": "uuid",
  "name": "Contact Form",
  "slug": "contact-form",
  "status": "draft"
}`,
    },
    {
      method: 'GET',
      path: '/api/forms/:id',
      description: 'Get a specific form by ID',
      auth: true,
      responseBody: `{
  "id": "uuid",
  "name": "Contact Form",
  "slug": "contact-form",
  "description": "A simple contact form",
  "status": "published",
  "schema": {
    "fields": [...]
  },
  "settings": {
    "submitButtonText": "Submit",
    "successMessage": "Thank you!"
  }
}`,
    },
    {
      method: 'PUT',
      path: '/api/forms/:id',
      description: 'Update a form',
      auth: true,
      requestBody: `{
  "name": "Updated Contact Form",
  "schema": {...},
  "settings": {...}
}`,
      responseBody: `{
  "id": "uuid",
  "name": "Updated Contact Form",
  "version": 2
}`,
    },
    {
      method: 'DELETE',
      path: '/api/forms/:id',
      description: 'Delete a form',
      auth: true,
      responseBody: `{
  "success": true
}`,
    },
  ],
  'Submissions': [
    {
      method: 'GET',
      path: '/api/forms/:id/submissions',
      description: 'List all submissions for a form',
      auth: true,
      responseBody: `{
  "submissions": [
    {
      "id": "uuid",
      "data": {
        "name": "John Doe",
        "email": "john@example.com",
        "message": "Hello!"
      },
      "metadata": {
        "submittedAt": "2024-01-01T00:00:00Z",
        "userAgent": "Mozilla/5.0..."
      },
      "created_at": "2024-01-01T00:00:00Z"
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 20,
    "total": 100,
    "totalPages": 5
  }
}`,
    },
    {
      method: 'POST',
      path: '/api/forms/:id/submissions',
      description: 'Create a new submission (public endpoint)',
      auth: false,
      requestBody: `{
  "data": {
    "name": "John Doe",
    "email": "john@example.com",
    "message": "Hello!"
  }
}`,
      responseBody: `{
  "success": true,
  "id": "uuid"
}`,
    },
    {
      method: 'GET',
      path: '/api/forms/:id/submissions/:submissionId',
      description: 'Get a specific submission',
      auth: true,
      responseBody: `{
  "id": "uuid",
  "data": {...},
  "metadata": {...},
  "created_at": "2024-01-01T00:00:00Z"
}`,
    },
    {
      method: 'DELETE',
      path: '/api/forms/:id/submissions/:submissionId',
      description: 'Delete a submission',
      auth: true,
      responseBody: `{
  "success": true
}`,
    },
  ],
  'API Keys': [
    {
      method: 'GET',
      path: '/api/api-keys',
      description: 'List all API keys for the user',
      auth: true,
      responseBody: `{
  "apiKeys": [
    {
      "id": "uuid",
      "name": "Production Key",
      "prefix": "fb_pk_xxx",
      "scopes": ["forms:read", "forms:write"],
      "lastUsedAt": "2024-01-01T00:00:00Z",
      "createdAt": "2024-01-01T00:00:00Z"
    }
  ]
}`,
    },
    {
      method: 'POST',
      path: '/api/api-keys',
      description: 'Create a new API key',
      auth: true,
      requestBody: `{
  "name": "Production Key",
  "scopes": ["forms:read", "forms:write", "submissions:read"]
}`,
      responseBody: `{
  "id": "uuid",
  "name": "Production Key",
  "key": "fb_pk_xxxxxxxxxxxxxxxx",
  "scopes": ["forms:read", "forms:write", "submissions:read"]
}`,
      example: 'The full key is only shown once. Store it securely.',
    },
    {
      method: 'DELETE',
      path: '/api/api-keys/:id',
      description: 'Revoke an API key',
      auth: true,
      responseBody: `{
  "success": true
}`,
    },
  ],
  'Groups': [
    {
      method: 'GET',
      path: '/api/groups',
      description: 'List all groups the user belongs to',
      auth: true,
      responseBody: `{
  "groups": [
    {
      "id": "uuid",
      "name": "Marketing Team",
      "description": "Marketing department forms",
      "role": "admin",
      "memberCount": 5
    }
  ]
}`,
    },
    {
      method: 'POST',
      path: '/api/groups',
      description: 'Create a new group',
      auth: true,
      requestBody: `{
  "name": "Marketing Team",
  "description": "Marketing department forms"
}`,
      responseBody: `{
  "id": "uuid",
  "name": "Marketing Team"
}`,
    },
    {
      method: 'POST',
      path: '/api/groups/:id/members',
      description: 'Add a member to a group',
      auth: true,
      requestBody: `{
  "userId": "uuid",
  "role": "member"
}`,
      responseBody: `{
  "success": true
}`,
    },
  ],
};

const methodColors: Record<string, string> = {
  GET: 'bg-green-100 text-green-700',
  POST: 'bg-blue-100 text-blue-700',
  PUT: 'bg-yellow-100 text-yellow-700',
  PATCH: 'bg-orange-100 text-orange-700',
  DELETE: 'bg-red-100 text-red-700',
};

export default function ApiDocsPage() {
  const [activeSection, setActiveSection] = useState<string>('Authentication');
  const [expandedEndpoint, setExpandedEndpoint] = useState<string | null>(null);

  const sections = Object.keys(endpoints);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-xl font-bold text-gray-900">FormBuilder</span>
              </Link>
              <span className="text-gray-300">|</span>
              <span className="text-gray-600 font-medium">API Documentation</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-gray-600 hover:text-gray-900 text-sm font-medium">
                Sign In
              </Link>
              <Link
                to="/register"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <nav className="sticky top-24 space-y-1">
              <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Endpoints
              </h3>
              {sections.map((section) => (
                <button
                  key={section}
                  onClick={() => setActiveSection(section)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeSection === section
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {section}
                </button>
              ))}

              <div className="pt-6">
                <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Resources
                </h3>
                <Link
                  to="/register"
                  className="block px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100"
                >
                  Get API Key
                </Link>
              </div>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Introduction */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">API Reference</h1>
              <p className="text-gray-600 mb-6">
                Welcome to the FormBuilder API documentation. Our RESTful API allows you to programmatically
                create forms, manage submissions, and integrate form functionality into your applications.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Base URL</h3>
                  <code className="text-sm bg-gray-200 px-2 py-1 rounded">
                    https://your-domain.com/api
                  </code>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Authentication</h3>
                  <code className="text-sm bg-gray-200 px-2 py-1 rounded">
                    Authorization: Bearer &lt;token&gt;
                  </code>
                </div>
              </div>
            </div>

            {/* Authentication Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Authentication</h2>
              <p className="text-gray-600 mb-4">
                The API supports two authentication methods:
              </p>
              <div className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">1. JWT Tokens (Recommended for web apps)</h4>
                  <p className="text-blue-800 text-sm">
                    Use the <code className="bg-blue-100 px-1 rounded">/api/auth/login</code> endpoint to obtain
                    an access token. Include it in the Authorization header for subsequent requests.
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 mb-2">2. API Keys (Recommended for server-to-server)</h4>
                  <p className="text-green-800 text-sm">
                    Generate an API key from your dashboard. Use it as a Bearer token in the Authorization header.
                    API keys don't expire but can be revoked at any time.
                  </p>
                </div>
              </div>
            </div>

            {/* Mobile Section Selector */}
            <div className="lg:hidden mb-6">
              <select
                value={activeSection}
                onChange={(e) => setActiveSection(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {sections.map((section) => (
                  <option key={section} value={section}>
                    {section}
                  </option>
                ))}
              </select>
            </div>

            {/* Endpoints */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-8 py-6 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">{activeSection}</h2>
              </div>

              <div className="divide-y divide-gray-200">
                {endpoints[activeSection]?.map((endpoint, index) => {
                  const endpointKey = `${endpoint.method}-${endpoint.path}`;
                  const isExpanded = expandedEndpoint === endpointKey;

                  return (
                    <div key={index} className="hover:bg-gray-50">
                      <button
                        onClick={() => setExpandedEndpoint(isExpanded ? null : endpointKey)}
                        className="w-full px-8 py-4 flex items-center gap-4 text-left"
                      >
                        <span className={`px-2 py-1 rounded text-xs font-bold ${methodColors[endpoint.method]}`}>
                          {endpoint.method}
                        </span>
                        <code className="text-sm font-mono text-gray-700 flex-1">
                          {endpoint.path}
                        </code>
                        {endpoint.auth && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                            Auth Required
                          </span>
                        )}
                        <svg
                          className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {isExpanded && (
                        <div className="px-8 pb-6 border-t border-gray-100 bg-gray-50">
                          <p className="text-gray-600 py-4">{endpoint.description}</p>

                          {endpoint.requestBody && (
                            <div className="mb-4">
                              <h4 className="text-sm font-semibold text-gray-700 mb-2">Request Body</h4>
                              <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">
                                {endpoint.requestBody}
                              </pre>
                            </div>
                          )}

                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Response</h4>
                            <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">
                              {endpoint.responseBody}
                            </pre>
                          </div>

                          {endpoint.example && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                              <strong>Note:</strong> {endpoint.example}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Rate Limiting */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mt-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Rate Limiting</h2>
              <p className="text-gray-600 mb-4">
                The API implements rate limiting to ensure fair usage and service stability.
              </p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 text-gray-600 font-medium">Endpoint Type</th>
                    <th className="text-left py-2 text-gray-600 font-medium">Limit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="py-2">Authentication endpoints</td>
                    <td className="py-2">10 requests per 15 minutes</td>
                  </tr>
                  <tr>
                    <td className="py-2">API endpoints (authenticated)</td>
                    <td className="py-2">1000 requests per hour</td>
                  </tr>
                  <tr>
                    <td className="py-2">Form submissions (public)</td>
                    <td className="py-2">5 requests per minute per IP</td>
                  </tr>
                </tbody>
              </table>
              <p className="text-gray-500 text-sm mt-4">
                Rate limit headers are included in all responses:
                <code className="bg-gray-100 px-1 rounded ml-1">X-RateLimit-Limit</code>,
                <code className="bg-gray-100 px-1 rounded ml-1">X-RateLimit-Remaining</code>,
                <code className="bg-gray-100 px-1 rounded ml-1">X-RateLimit-Reset</code>
              </p>
            </div>

            {/* Error Codes */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mt-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Error Codes</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 text-gray-600 font-medium">Code</th>
                    <th className="text-left py-2 text-gray-600 font-medium">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="py-2"><code className="bg-gray-100 px-2 rounded">400</code></td>
                    <td className="py-2">Bad Request - Invalid parameters or request body</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code className="bg-gray-100 px-2 rounded">401</code></td>
                    <td className="py-2">Unauthorized - Missing or invalid authentication</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code className="bg-gray-100 px-2 rounded">403</code></td>
                    <td className="py-2">Forbidden - Insufficient permissions</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code className="bg-gray-100 px-2 rounded">404</code></td>
                    <td className="py-2">Not Found - Resource doesn't exist</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code className="bg-gray-100 px-2 rounded">429</code></td>
                    <td className="py-2">Too Many Requests - Rate limit exceeded</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code className="bg-gray-100 px-2 rounded">500</code></td>
                    <td className="py-2">Internal Server Error - Something went wrong</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* SDKs */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl p-8 mt-8 text-white">
              <h2 className="text-xl font-bold mb-4">Need Help?</h2>
              <p className="text-blue-100 mb-6">
                Have questions about the API or need assistance with integration?
                Our team is here to help.
              </p>
              <div className="flex gap-4">
                <Link
                  to="/register"
                  className="px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                >
                  Get Started
                </Link>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
